import type { Weekday, WorkingDay } from '@wtfalch/hr';

/**
 * Working hours as `@wtfalch/hr` returns them: per weekday a wall-clock start
 * and end in an IANA time zone. A weekday with no entry is a day off. Only the
 * types come from the package (erased at build): its index also exports the
 * server-side authorization helpers, which the browser bundle must not pull in.
 */
export type { Weekday, WorkingDay };
export const WEEKDAYS: readonly Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const SHORT_WEEKDAY: Record<string, Weekday> = {
  Mon: 'mon',
  Tue: 'tue',
  Wed: 'wed',
  Thu: 'thu',
  Fri: 'fri',
  Sat: 'sat',
  Sun: 'sun',
};

/** The weekday and `HH:MM` wall clock it is at `at` in `timeZone`. The zone's
 *  own rules do the work, so a day with a daylight-saving jump is the length
 *  that zone says it is. */
function wallClock(at: Date, timeZone: string): { weekday: Weekday; time: string } | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(at);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const weekday = SHORT_WEEKDAY[get('weekday')];
    if (!weekday) return null;
    return { weekday, time: `${get('hour')}:${get('minute')}` };
  } catch {
    // An unknown zone name: nothing can be said, so it is not "inside".
    return null;
  }
}

/**
 * Whether `at` falls inside the person's working hours. Each day's start and
 * end are read in that day's own time zone, so someone in Oslo and someone in
 * Lisbon are each judged against their own clock, and the two clock changes a
 * year neither double-count nor skip a shift (a shift cannot cross midnight,
 * so a wall-clock comparison within one local day is exact). The end is
 * exclusive: at 17:00 sharp a 09:00-17:00 shift is over. A person with no
 * hours set is not inside any.
 */
export function insideWorkingHours(days: readonly WorkingDay[], at: Date = new Date()): boolean {
  return days.some((day) => {
    const now = wallClock(at, day.timeZone);
    return (
      now !== null && now.weekday === day.weekday && day.start <= now.time && now.time < day.end
    );
  });
}

/** `09:00-17:00` for each distinct shift, weekdays collapsed: `Mon-Fri 09:00-17:00`. */
export function summariseHours(days: readonly WorkingDay[]): string {
  const groups = new Map<string, Weekday[]>();
  for (const d of WEEKDAYS) {
    const day = days.find((x) => x.weekday === d);
    if (!day) continue;
    const key = `${day.start}-${day.end}`;
    groups.set(key, [...(groups.get(key) ?? []), d]);
  }
  const label = (w: Weekday) => w[0]?.toUpperCase() + w.slice(1);
  return [...groups]
    .map(([range, ws]) => {
      const runs: string[] = [];
      let from = 0;
      for (let i = 1; i <= ws.length; i++) {
        const prev = ws[i - 1] as Weekday;
        const cur = ws[i];
        if (cur && WEEKDAYS.indexOf(cur) === WEEKDAYS.indexOf(prev) + 1) continue;
        const first = ws[from] as Weekday;
        runs.push(i - from > 1 ? `${label(first)}-${label(prev)}` : label(first));
        from = i;
      }
      return `${runs.join(', ')} ${range}`;
    })
    .join(' · ');
}
